import React, { useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Play } from "@phosphor-icons/react";
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const INK = '#2A1E5C';
const INK2 = '#4A4458';
const MUTED = '#8B7E6A';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';
const LINE = 'rgba(42,30,92,0.10)';

const DO_ITEMS = [
  'Find or refine the weekly number you’re working toward.',
  'Log earnings in the app and follow four relays in your own Google Sheet.',
  'Tell us what helps, what confuses you, and what you’d change.',
];

// Accurate to the live checkout config: the BETA Sprint takes NO card up front. The free
// 28-day trial simply cancels at day 28 if no card was added (no surprise charge). Beta
// members who choose to continue get a 50%-off-for-life coupon (duration=forever) — $9.50/mo.

export default function BetaChallengePage() {
  const [params] = useSearchParams();
  // Pass a tracked outreach source through to checkout metadata (source_page),
  // and enter the existing Free Sprint beta path (beta=1) with no new Stripe objects.
  const src = params.get('src');
  const joinHref = `/start/free-sprint?beta=1${src ? `&src=${encodeURIComponent(src)}` : ''}`;

  const [videoOn, setVideoOn] = useState(false);
  const videoRef = useRef(null);
  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.controls = true;
    v.currentTime = 0;
    v.play().catch(() => {});
    setVideoOn(true);
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="beta-challenge-page">
      <SiteNav />

      {/* ---------- Hero: full-width brand-yellow band, deep-purple type ---------- */}
      <section className="relative w-full overflow-hidden" style={{ background: GOLD }} data-testid="beta-hero">
        {/* wavy checkered finish-line flag watermark — transparent cutout, soft and subtle so yellow stays dominant */}
        <img
          src="/images/checkered-flag.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-16 -right-24 w-[78%] max-w-4xl rotate-[-7deg]"
          style={{ opacity: 0.12 }}
          data-testid="beta-hero-flag"
        />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pt-12 pb-14 sm:pt-16 lg:pt-20 lg:pb-20 lg:grid lg:grid-cols-[1.02fr_0.98fr] lg:gap-14 lg:items-stretch">
          <div className="lg:flex lg:flex-col lg:justify-center">
            <p className="font-body text-[11px] uppercase tracking-[0.26em] font-bold mb-4" style={{ color: 'rgba(42,30,92,0.65)' }} data-testid="beta-eyebrow">
              Payday Relay Beta
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="beta-heading">
              The Four-Relay Beta Challenge
            </h1>
            <p className="text-lg sm:text-xl font-bold mb-4" style={{ color: INK }} data-testid="beta-tagline">
              Give your income a weekly target, and give that target a job.
            </p>
            <p className="text-base sm:text-lg leading-relaxed mb-8 max-w-xl" style={{ color: 'rgba(42,30,92,0.85)' }} data-testid="beta-intro">
              Maybe you already know when to close your laptop. Maybe your workday keeps stretching.
              Either way, you need to know what this week’s earnings must cover. Use your real numbers
              to find a weekly target, plan your set-asides, and test that plan across four weeks of
              independent work.
            </p>

            <Link
              to={joinHref}
              className="group inline-flex self-start w-fit items-center gap-2 px-9 py-4 rounded-full font-bold text-base sm:text-lg transition-transform hover:scale-[1.02] shadow-lg"
              style={{ background: INK, color: '#FFFFFF' }}
              data-testid="beta-join-cta"
            >
              Join the Challenge
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>

            <p className="text-sm leading-relaxed mt-4 max-w-md" style={{ color: 'rgba(42,30,92,0.7)' }} data-testid="beta-hero-disclosure">
              Free for 28 days — no card needed to start. Run all four relays, then continue only if
              you want to. As a founding beta member you keep <strong>50% off for life — $9.50/mo</strong> instead
              of $19, for as long as you stay.
            </p>
          </div>

          <figure className="mt-12 lg:mt-0 lg:h-full lg:flex lg:flex-col lg:justify-center" data-testid="beta-product-shot">
            <div className="relative mx-auto w-full max-w-xl">
              {/* laptop lid / screen */}
              <div className="rounded-t-xl p-2.5 sm:p-3 shadow-2xl" style={{ background: INK }}>
                <div className="rounded-md overflow-hidden bg-white">
                  <img
                    src="/images/sprint-weekly-target.png"
                    alt="A real Payday Relay app screen showing a weekly income target of $1,521.74 and the take-home it needs to cover"
                    loading="eager"
                    className="w-full block"
                    data-testid="beta-product-image"
                  />
                </div>
              </div>
              {/* laptop base / hinge */}
              <div className="relative mx-auto" style={{ width: '116%', marginLeft: '-8%' }}>
                <div className="h-3.5 sm:h-4 rounded-b-xl shadow-lg" style={{ background: '#241a4f' }} />
                <div className="mx-auto h-1.5 rounded-b-md" style={{ width: '16%', background: '#15103a' }} />
              </div>
            </div>
            <figcaption className="text-xs mt-5 text-center" style={{ color: 'rgba(42,30,92,0.6)' }}>
              A real Sprint result — your weekly target and what it needs to cover.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ---------- Demo video: click to play, no autoplay ---------- */}
      <section className="px-5 sm:px-6 lg:px-8 pt-14 sm:pt-16" data-testid="beta-video-section">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] mb-5" style={{ color: MUTED }} data-testid="beta-video-label">
            See Payday Relay in action
          </p>
          <div
            className="relative w-full rounded-[1.5rem] overflow-hidden"
            style={{ aspectRatio: '16 / 9', background: INK, boxShadow: '0 24px 50px rgba(42,30,92,0.25)' }}
            data-testid="beta-video-frame"
          >
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              preload="metadata"
              data-testid="beta-explainer-video-player"
            >
              <source src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/5cc0ad01-9dd9-4016-b0aa-c22bc26dbf56/attachments/cc7eb30f725d41c49e26a63da2c99c29_payday_relay_explainer_v6.mp4" type="video/mp4" />
              <track kind="captions" src="/captions/payday-relay-explainer.vtt" srcLang="en" label="English" default />
            </video>
            {!videoOn && (
              <button
                type="button"
                onClick={handlePlay}
                aria-label="Play the Payday Relay demo video"
                className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors"
                data-testid="beta-video-play-button"
              >
                <span className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ background: 'rgba(245,203,92,0.95)' }}>
                  <Play className="w-8 h-8 ml-1" style={{ color: INK }} weight="fill" />
                </span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Editorial body: unframed, concise, generous spacing ---------- */}
      <section className="px-5 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <div className="max-w-2xl mx-auto">

          <div className="pt-10 border-t" style={{ borderColor: LINE }} data-testid="beta-whatyoulldo">
            <h2 className="text-2xl sm:text-3xl mb-5 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              What you’ll do
            </h2>
            <ul className="space-y-3.5">
              {DO_ITEMS.map((line) => (
                <li key={line} className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                  <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-10 mt-10 border-t" style={{ borderColor: LINE }} data-testid="beta-whoitsfor">
            <h2 className="text-2xl sm:text-3xl mb-4 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              Who it’s for
            </h2>
            <p className="text-base leading-relaxed" style={{ color: INK2 }}>
              Freelancers, gig and platform workers, and independent service providers, including
              hands-on practitioners. You don’t have to identify with any one label, and no ADHD
              diagnosis is required.
            </p>
          </div>

          <div className="pt-10 mt-10 border-t" style={{ borderColor: LINE }} data-testid="beta-agreement">
            <h2 className="text-2xl sm:text-3xl mb-4 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              The beta agreement
            </h2>
            <p className="text-base leading-relaxed" style={{ color: INK2 }}>
              Payday Relay is still being tested. You may encounter bugs or confusing moments, and we’ll
              send occasional feedback requests. Honest criticism is welcome. A testimonial is never required.
            </p>
          </div>

          <div className="pt-10 mt-10 border-t" style={{ borderColor: LINE }} data-testid="beta-terms">
            <h2 className="text-2xl sm:text-3xl mb-5 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              The details
            </h2>
            <ul className="space-y-3.5">
              <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                <span>Free to start — no card, no commitment. You won't be asked for payment to join.</span>
              </li>
              <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                <span>All four relays — a full 28 days — are free.</span>
              </li>
              <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                <span>When the 28 days are up, nothing happens automatically — your Sprint simply ends and you're never charged.</span>
              </li>
              <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                <span>
                  Want to keep going? As a founding beta member you lock in{' '}
                  <strong style={{ color: INK }}>50% off for life — $9.50/mo</strong> instead of the $19/mo
                  plan, for as long as you stay a member.
                </span>
              </li>
              <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: INK2 }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                <span>Cancel any time, in one click. Your sheet always stays yours.</span>
              </li>
            </ul>
            <p className="text-xs leading-relaxed mt-6" style={{ color: MUTED }} data-testid="beta-fineprint">
              We’ll email you before your Sprint ends. Payday Relay is an educational planning system,
              not financial, tax, legal, or investment advice.
            </p>
          </div>

        </div>
      </section>

      {/* ---------- Bottom CTA so the action is always within reach ---------- */}
      <section className="px-5 sm:px-6 lg:px-8 pb-20 sm:pb-28" data-testid="beta-bottom-cta">
        <div className="max-w-2xl mx-auto text-center pt-12 border-t" style={{ borderColor: LINE }}>
          <h2 className="text-2xl sm:text-3xl mb-4 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
            Ready to run four relays?
          </h2>
          <p className="text-base leading-relaxed mb-7 max-w-md mx-auto" style={{ color: INK2 }}>
            Start free today and see what this week’s earnings really need to cover.
          </p>
          <Link
            to={joinHref}
            className="group inline-flex items-center gap-2 px-9 py-4 rounded-full font-bold text-base sm:text-lg transition-transform hover:scale-[1.02] shadow-md"
            style={{ background: INK, color: '#FFFFFF' }}
            data-testid="beta-join-cta-bottom"
          >
            Join the Challenge
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="text-sm leading-relaxed mt-4 max-w-md mx-auto" style={{ color: MUTED }}>
            Free for 28 days, no card needed. Keep going only if you want — founding beta members lock in
            50% off for life ($9.50/mo). Cancel any time.
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
